import React, { useEffect, useState } from "react";
import SrFaceMatchCard from "./sr-face-match-card";
import SrFaceMatchActions from "./sr-face-match-actions";
import SrFaceCheckLoading from "./sr-face-check-loading";
import type { SrFaceCheckResult, SrFaceDecision } from "./sr-face-check-types";

type Props = {
    check: SrFaceCheckResult;
    previewSrc?: string | null;
    busy: boolean;
    onDecide: (voterid: string, decision: SrFaceDecision) => void;
    onSkipNoMatch: () => void;
};

const SrFaceMatchPanel = ({ check, previewSrc, busy, onDecide, onSkipNoMatch }: Props) => {
    const matches = check.matches || [];
    const captured = check.captured_photo || previewSrc || "";
    const [selectedVoterId, setSelectedVoterId] = useState("");

    const firstVoterId = String(matches[0]?.voterid || "");

    useEffect(() => {
        setSelectedVoterId(firstVoterId);
    }, [firstVoterId]);

    if (busy) {
        return <SrFaceCheckLoading message="Saving decision..." detail="Updating voter photo choice." />;
    }

    if (matches.length === 0) {
        return (
            <div className="rounded-xl bg-green-50 p-5 text-center">
                <p className="text-sm font-semibold text-green-900">No matching faces found</p>
                <p className="mt-1 text-xs text-green-700">
                    This photo can be saved for the current voter.
                </p>
                <button
                    type="button"
                    onClick={onSkipNoMatch}
                    className="mt-4 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                >
                    Continue
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900">
                    Possible match{matches.length > 1 ? "es" : ""} found
                </p>
                <p className="mt-1 text-xs text-amber-800">
                    Pick one voter, then use Replace or Keep once for that voter.
                </p>
            </div>

            {captured ? (
                <div className="flex items-center gap-3">
                    <div className="h-14 w-14 overflow-hidden rounded-lg bg-gray-100">
                        <img src={captured} alt="Captured" className="h-full w-full object-cover" />
                    </div>
                    <p className="text-xs text-gray-500">Captured photo</p>
                </div>
            ) : null}

            <div className="max-h-[40vh] space-y-2 overflow-y-auto pr-1">
                {matches.map((match, index) => {
                    const id = String(match.voterid || index);
                    return (
                        <SrFaceMatchCard
                            key={id}
                            match={match}
                            selected={selectedVoterId === String(match.voterid || "")}
                            disabled={busy}
                            onSelect={setSelectedVoterId}
                        />
                    );
                })}
            </div>

            <SrFaceMatchActions
                busy={busy}
                canDecide={Boolean(selectedVoterId)}
                onDecide={(decision) => onDecide(selectedVoterId, decision)}
            />
        </div>
    );
};

export default SrFaceMatchPanel;
